<template>
  <div
    class="flex flex-col gap-3 bg-white border border-[#d8d8e2] rounded-lg p-4 text-left"
    data-testid="check-validation-summary-card"
  >
    <div class="flex items-start justify-between gap-3">
      <DoctorInfo :name="doctorName" :subtitle="crmLabel ?? DISPLAY_FALLBACK" size="md" />
      <CheckValidationStatusBadge
        :status="row.status"
        :is-overdue="row.isOverdue"
        :is-edited-since-submit="isEditedSinceSubmit"
      />
    </div>

    <dl class="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
      <div class="col-span-2 flex flex-col gap-0.5">
        <dt class="text-xs text-neutral50">Unidade · Escala</dt>
        <dd class="font-bold text-textPrimary">{{ row.unit }} · {{ row.scheduleLabel }}</dd>
      </div>

      <div class="flex flex-col gap-0.5">
        <dt class="text-xs text-neutral50">Data do plantão</dt>
        <dd class="text-textPrimary">{{ row.stationDate }}</dd>
      </div>

      <div class="flex flex-col gap-0.5">
        <dt class="text-xs text-neutral50">Previsto</dt>
        <dd class="text-textPrimary" data-testid="check-validation-summary-expected">
          {{ row.expectedRange }}
        </dd>
      </div>

      <div class="flex flex-col gap-0.5">
        <dt class="text-xs text-neutral50">Digitado</dt>
        <dd class="font-bold text-textPrimary" data-testid="check-validation-summary-typed">
          {{ typedRange }}
        </dd>
      </div>

      <div class="flex flex-col gap-0.5">
        <dt class="text-xs text-neutral50">Diferença</dt>
        <dd
          :class="diffMinutes ? 'font-bold text-textPrimary' : 'text-neutral50'"
          data-testid="check-validation-summary-diff"
        >
          {{ diffLabel }}
        </dd>
      </div>

      <div class="flex flex-col gap-0.5">
        <dt class="text-xs text-neutral50">Lançado por</dt>
        <dd class="text-textPrimary">{{ row.createdBy?.name ?? DISPLAY_FALLBACK }}</dd>
      </div>

      <div class="flex flex-col gap-0.5">
        <dt class="text-xs text-neutral50">Enviado para análise</dt>
        <dd class="text-textPrimary">
          {{ submittedAtLabel }}
          <span v-if="row.queueTimeLabel !== DISPLAY_FALLBACK" class="text-neutral50">
            ({{ row.queueTimeLabel }} em fila)
          </span>
        </dd>
      </div>
    </dl>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import DoctorInfo from '@/components/DoctorInfo.vue'
import { formatDateTimeBR } from '@/utils/date'

import CheckValidationStatusBadge from './CheckValidationStatusBadge.vue'
import {
  DISPLAY_FALLBACK,
  computeDiffMinutes,
  formatCrmLabel,
  formatDiffMinutes,
  formatHourRange
} from '../mappers'
import type { CheckValidationRow } from '../types'

interface TypedHours {
  checkin: string | null
  checkout: string | null
}

const props = withDefaults(
  defineProps<{
    row: CheckValidationRow
    typedHours?: TypedHours | null
    timezone?: string
    doctorNameOverride?: string | null
    crm?: { number: string; uf: string } | null
  }>(),
  { typedHours: null, timezone: '', doctorNameOverride: null, crm: null }
)

const doctorName = computed(() => props.doctorNameOverride ?? props.row.doctor.name)

const crmLabel = computed(
  () => props.row.doctor.crmLabel ?? formatCrmLabel(props.crm?.number, props.crm?.uf)
)

const typedRange = computed(() =>
  props.typedHours
    ? formatHourRange(props.typedHours.checkin, props.typedHours.checkout, props.timezone)
    : props.row.typedRange
)

const diffMinutes = computed(() => {
  if (!props.typedHours) return props.row.diffMinutes
  return computeDiffMinutes(
    { start: props.row.expectedStartIso, end: props.row.expectedEndIso },
    { checkin: props.typedHours.checkin, checkout: props.typedHours.checkout }
  )
})

const diffLabel = computed(() =>
  props.typedHours ? formatDiffMinutes(diffMinutes.value) : props.row.diffLabel
)

// O `EDITED_DURING_REVIEW` chega pela fila e pelo detalhe; a row já reflete.
const isEditedSinceSubmit = computed(() => props.row.isEditedSinceSubmit)

const submittedAtLabel = computed(() =>
  props.row.submittedAtIso ? formatDateTimeBR(props.row.submittedAtIso) : DISPLAY_FALLBACK
)
</script>
